import { useUserInformationContext } from 'contexts/userContext';
import { usePreferences } from 'contexts/preferences-context';
import { listOrders } from 'graphql/orders';
import { useEffect, useRef, useReducer, useState } from 'react';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from 'react-query';
import { useHistory } from 'react-router-dom';
import { ListOrdersParams } from 'types/common';
import { OrderListingType } from 'types/order';

import { CurrentData, FiltersType, intialReducerState, reducerModals, ViewType } from './services';
import { CURRENT_PATH, DEFAULT_ROUTE } from 'utils/constants';
import { useInfoUpdateEvent } from 'hooks/useInfoUpdateEvent';

export type UseOrdersReturnType = {
  t: TFunction<'translation', undefined>;
  filter: FiltersType;
  orders: OrderListingType[];
  loading: boolean;
  currentData: CurrentData;
  agencyName: string;
  state:
    | {
        mode: string;
        data?: undefined;
      }
    | {
        mode: string;
        data: any;
      }
    | {
        mode: null;
        data: null;
      };
  handleModalOpen: (param: boolean) => void;
  handleChangeFilter: (value: FiltersType) => void;
  handleSelectRow: (id: string, code: string) => void;
  handleTablePageChange: (action: 'prev' | 'next') => (cuantity?: number) => void;
  viewTabValue: ViewType;
  handleViewTabValueChange: (value: ViewType) => void;
  agencySelectedId?: string;
};

export const useOrders = (agencySelectedId?: string): UseOrdersReturnType => {
  const history = useHistory();
  const { t } = useTranslation();
  const { currentUser } = useUserInformationContext();
  const { activateOrder, ordersView, setOrdersView } = usePreferences() as any;
  const [filter, setFilter] = useState<FiltersType>('open');
  const [state, dispatch] = useReducer(reducerModals, intialReducerState);
  const queryCache = useQueryClient();

  const handleViewTabValueChange = (value: ViewType) => {
    setOrdersView(value);
  };

  const [params, setParams] = useState<ListOrdersParams>({
    agencyId: agencySelectedId,
    searchText: '',
    pagination: {
      items: 10,
      page: 1,
    },
  });

  const ordersRef = useRef<OrderListingType[]>([]);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useInfoUpdateEvent((data: { kind: string, update?: { _id: string } }) => {
    if (data.kind === 'createOrder') {
      queryCache.invalidateQueries({
        predicate: q => q.queryKey.includes('orders'),
      });
      return;
    }

    const matchingKinds = [
      'createNewProperty',
      'deleteProperty',
      'updateProperty',
      'createAssociation',
      'deleteAssociacion',
    ];
    if (!matchingKinds.includes(data.kind)) return;
    if (data.update?._id == null) return;

    const currentOrders = ordersRef.current;
    if (!currentOrders.some(order => order._id === data.update!._id)) return;

    if (timeoutRef.current != null) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      queryCache.invalidateQueries({
        predicate: q => q.queryKey.includes('orders'),
      });
    }, 200);
  });

  useEffect(() => {
    return () => {
      if (timeoutRef.current != null) clearTimeout(timeoutRef.current);
    };
  }, []);

  const getOrders = useQuery([params, 'orders'], () => listOrders(params), {
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    enabled: Object.keys(params).length > 0,
  });

  useEffect(() => {
    ordersRef.current = getOrders.data?.listOrders.orders || [];
  }, [getOrders.data]);

  const agencyName =
    currentUser?.accesses.find((access) => access._id === agencySelectedId)?.name || '';

  useEffect(() => {
    setParams((prev) => ({
      ...prev,
      agencyId: agencySelectedId,
      pagination: {
        ...prev.pagination,
        page: 1,
      },
    }));
  }, [agencySelectedId]);

  const orders = getOrders.data?.listOrders.orders || [];
  const currentData = {
    page: getOrders.data?.listOrders.pagination.page || 0,
    pages: getOrders.data?.listOrders.pagination.pages || 0,
  };

  const handleModalOpen = (param: boolean) => {
    if (param) {
      dispatch({ type: 'new-order' });
    } else {
      dispatch({ type: null });
    }
  };

  const handleChangeFilter = (value: FiltersType) => {
    setFilter(value);
    setParams((prev) => {
      const data: ListOrdersParams = {
        ...prev,
        pagination: {
          ...prev.pagination,
          page: 1,
        },
      };

      if (value === 'all') {
        delete data.status;
        return { ...data };
      }

      return {
        ...data,
        status: value,
      };
    });
  };

  const handleTablePageChange = (action: 'prev' | 'next') => {
    if (action === 'next' && currentData.page < currentData.pages) {
      return (cuantity = 1) => {
        setParams((prev) => ({
          ...prev,
          pagination: {
            ...prev.pagination,
            page: prev.pagination.page + cuantity,
          },
        }));
      };
    }

    if (action === 'prev' && currentData.page > 1) {
      return (cuantity = 1) => {
        setParams((prev) => ({
          ...prev,
          pagination: {
            ...prev.pagination,
            page: prev.pagination.page - cuantity,
          },
        }));
      };
    }

    return () => {};
  };

  const handleSelectRow = (id: string, code: string) => {
    localStorage.setItem(CURRENT_PATH, JSON.stringify({ view: DEFAULT_ROUTE, param: '' }));
    history.push(`/orders/details/${id}/${DEFAULT_ROUTE}`);

    activateOrder({ id, orderId: code, firstPropertyAddress: '' });
  };

  return {
    t,
    filter,
    orders,
    loading: getOrders.isFetching,
    currentData,
    agencyName,
    state,
    viewTabValue: ordersView,
    handleViewTabValueChange,
    handleModalOpen,
    handleChangeFilter,
    handleSelectRow,
    handleTablePageChange,
    agencySelectedId,
  };
};
